import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { TabBar } from '../components/ui';
import { Deposit } from './Deposit';
import { Transfer } from './Transfer';
import { Withdraw } from './Withdraw';

type Tab = 'deposit' | 'transfer' | 'withdraw';

const TABS: readonly Tab[] = ['deposit', 'transfer', 'withdraw'];

/**
 * Unified wallet page — renders the Deposit / Transfer / Withdraw
 * subviews under a single /wallet route controlled by ?tab=. Keeps
 * the old routes as redirects (in App.tsx).
 */
export function Wallet() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab: Tab = (TABS as readonly string[]).includes(raw ?? '')
    ? (raw as Tab)
    : 'deposit';

  return (
    <div className="mx-auto max-w-app px-5 pt-6">
      <h1 className="pb-display text-[28px] leading-tight">
        {t('wallet.title')}
      </h1>
      <p className="mt-1.5 text-[14px] leading-relaxed text-tg-hint">
        {t('wallet.subtitle')}
      </p>
      <div className="mt-5">
        <TabBar
          value={tab}
          fullWidth
          size="lg"
          onChange={(v) =>
            setParams((prev) => {
              const next = new URLSearchParams(prev);
              next.set('tab', v);
              return next;
            })
          }
          options={[
            { value: 'deposit', label: t('wallet.tab_deposit') },
            { value: 'transfer', label: t('wallet.tab_transfer') },
            { value: 'withdraw', label: t('wallet.tab_withdraw') },
          ]}
        />
      </div>
      <div className="mt-2 -mx-5">
        {tab === 'deposit' ? (
          <Deposit embedded />
        ) : tab === 'transfer' ? (
          <Transfer embedded />
        ) : (
          <Withdraw embedded />
        )}
      </div>
    </div>
  );
}
